import { apiClient, LONG_TIMEOUT_MS } from './client'
import type {
  CatalogDocumentsResponseDTO,
  DocumentDeleteResponseDTO,
  DocumentKind,
  IngestResponseDTO,
} from './types'

/**
 * Documents API surface (Step 7.9).
 *
 * Wraps two existing backend endpoints used by the legacy
 * ``RAG/frontend`` admin:
 *
 *   - ``GET  /catalog/documents?tenant_id=…`` for listing.
 *   - ``POST /ingest`` (multipart/form-data) for uploads.
 *
 * Auth follows the rest of the chat client: HttpOnly session cookies via
 * ``credentials: 'include'`` (handled by ``apiClient``). The frontend never
 * reads or attaches tokens. ``tenant_id`` is always derived from the
 * authenticated organization on the caller side — we never accept it from
 * untrusted form input.
 */

export interface ListDocumentsScope {
  tenantId: string
  entityType?: string | null
  entityId?: string | null
  groupType?: string | null
  groupId?: string | null
  /**
   * Step 8.0 — filtrar por discriminador:
   *   'case_document'      → apenas documentos de paciente/cliente/caso.
   *   'reference_document' → apenas Biblioteca de Referência.
   *   undefined            → todos (compatibilidade legada).
   */
  documentKind?: DocumentKind
}

export interface UploadDocumentPayload {
  file: File
  tenantId: string
  entityType: string
  entityId: string
  groupType?: string | null
  groupId?: string | null
  documentId?: string | null
  /** Step 9.5 — optional human display names; backend keeps IDs immutable. */
  entityDisplayName?: string | null
  groupDisplayName?: string | null
  /** Optional friendly title (already supported on the backend). */
  title?: string | null
}

export interface DeleteDocumentPayload {
  tenantId: string
  documentId: string
}

export const documentsApi = {
  listDocuments: (params: ListDocumentsScope, signal?: AbortSignal) =>
    apiClient.get<CatalogDocumentsResponseDTO>(
      '/catalog/documents',
      {
        tenant_id: params.tenantId,
        entity_type: params.entityType ?? undefined,
        entity_id: params.entityId ?? undefined,
        group_type: params.groupType ?? undefined,
        group_id: params.groupId ?? undefined,
        document_kind: params.documentKind ?? undefined,
      },
      signal,
    ),

  uploadDocument: (payload: UploadDocumentPayload, signal?: AbortSignal) => {
    const form = new FormData()
    form.append('file', payload.file)
    form.append('tenant_id', payload.tenantId)
    form.append('entity_type', payload.entityType)
    form.append('entity_id', payload.entityId)
    if (payload.groupType) form.append('group_type', payload.groupType)
    if (payload.groupId) form.append('group_id', payload.groupId)
    if (payload.documentId) form.append('document_id', payload.documentId)
    if (payload.title) form.append('title', payload.title)
    if (payload.entityDisplayName)
      form.append('entity_display_name', payload.entityDisplayName)
    if (payload.groupDisplayName)
      form.append('group_display_name', payload.groupDisplayName)
    // Ingestion runs embeddings server-side and can take far longer than a
    // CRUD call — use the extended ceiling so a real upload is never cut, but
    // still bound it so a stalled upload can't spin the panel forever.
    return apiClient.postForm<IngestResponseDTO>('/ingest', form, {
      signal,
      timeoutMs: LONG_TIMEOUT_MS,
    })
  },

  /**
   * 7.9C — Removes a document together with its indexed chunks. ``tenantId``
   * is derived from the authenticated organization on the caller side; we
   * never accept it from untrusted UI state. The backend enforces tenant
   * isolation independently — this client always passes the value as a query
   * param because the existing catalog routes were designed that way.
   */
  deleteDocument: (payload: DeleteDocumentPayload, signal?: AbortSignal) => {
    const documentId = encodeURIComponent(payload.documentId)
    return apiClient.del<DocumentDeleteResponseDTO>(
      `/catalog/documents/${documentId}`,
      {
        query: { tenant_id: payload.tenantId },
        signal,
      },
    )
  },
}
